import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Star, Home, RotateCcw, Ticket as TicketIcon, Gift, TrendingUp, Lock, DoorOpen, DoorClosed, ScanLine } from "lucide-react";
import api, { imgUrl } from "@/lib/api";

// Planimetria: stage 960x520
const ZONES = {
  INGRESSO: { x: 20, y: 40, w: 150, h: 150, label: "Ingresso", c: "#22c55e" },
  DESK: { x: 20, y: 210, w: 150, h: 140, label: "Desk / Cassa", c: "#38bdf8" },
  USCITA: { x: 20, y: 370, w: 150, h: 110, label: "Uscita", c: "#f87171" },
  SLOT: { x: 360, y: 40, w: 250, h: 150, label: "Sala Slot", c: "#a78bfa" },
  VLT: { x: 640, y: 40, w: 300, h: 150, label: "Sala VLT", c: "#f5c518" },
  BAR: { x: 360, y: 330, w: 250, h: 150, label: "Area Relax / Bar", c: "#fb923c" },
  SCOMMESSE: { x: 640, y: 330, w: 300, h: 150, label: "Area Scommesse", c: "#2dd4bf" },
};
const CENTER = {
  INGRESSO: [95, 115], DESK: [95, 280], USCITA: [75, 425],
  SLOT: [485, 115], VLT: [790, 115], BAR: [485, 405], SCOMMESSE: [790, 405],
  OUT: [-120, 425],
};

const ROLES = ["A", "B", "C", "D", "E", "F"];
const CLUSTER_COLOR = { STEEL: "#9ca3af", BRONZE: "#c2703b", SILVER: "#c0c0c0", GOLD: "#d4af37", PLATINUM: "#67e8f9", NEW: "#9ca3af" };
const initials = (c) => `${(c.nome || "?")[0] || ""}${(c.cognome || "")[0] || ""}`.toUpperCase();

function CountUp({ from, to, dur = 2.2, className }) {
  const [v, setV] = useState(from);
  useEffect(() => {
    let raf; const start = performance.now();
    const tick = (now) => {
      const p = Math.min((now - start) / (dur * 1000), 1);
      setV(Math.round(from + (to - from) * p));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [from, to, dur]);
  return <span className={className}>{v}</span>;
}

export default function PlusDemo() {
  const navigate = useNavigate();
  const [chars, setChars] = useState(null);
  const [rewardDemo, setRewardDemo] = useState({ nome: "Drink", points: 5, image: null });
  const [i, setI] = useState(0);
  const [phase, setPhase] = useState("loading"); // loading | playing | done
  const [typed, setTyped] = useState("");
  const timerRef = useRef(null);

  // ---- carica dati REALI (sola lettura) + snapshot in memoria ----
  useEffect(() => {
    (async () => {
      try {
        const [cs, rw] = await Promise.all([
          api.get("/plus/customers?sort=cv&limit=100"),
          api.get("/plus/rewards").catch(() => ({ data: [] })),
        ]);
        const list = cs.data || [];
        const byLevel = (lv) => list.find((c) => c.cv_level === lv);
        const redeemer = [...list].sort((a, b) => (b.balance || 0) - (a.balance || 0))[0];
        const ticketC = [...list].sort((a, b) => (b.cv_score || 0) - (a.cv_score || 0))[0];
        const newC = [...list].sort((a, b) => (a.visits_30d || 0) - (b.visits_30d || 0) || (a.cv_score || 0) - (b.cv_score || 0))[0];
        const pick = { A: redeemer, B: ticketC, C: byLevel("BRONZE"), D: byLevel("SILVER"), E: byLevel("GOLD") || byLevel("PLATINUM"), F: newC };
        // dedup fallback
        const fb = (idx) => list[idx % Math.max(list.length, 1)] || { nome: "Cliente", cognome: `${idx}`, balance: 10, cv_score: 40, cv_level: "SILVER", id: `demo-${idx}` };
        Object.keys(pick).forEach((k, idx) => { if (!pick[k]) pick[k] = fb(idx); });
        setChars(pick);
        const rewards = rw.data || [];
        const drink = rewards.find((r) => /drink|whisky|bibita|caff|aperit|bar/i.test(r.nome)) || [...rewards].sort((a, b) => a.points_required - b.points_required)[0];
        if (drink) setRewardDemo({ nome: drink.nome, points: drink.points_required, image: drink.image_url || null });
        setPhase("playing");
      } catch {
        setChars({ A: { nome: "Cliente", cognome: "A", balance: 18, cv_level: "SILVER", id: "a" }, B: { nome: "Cliente", cognome: "B", cv_score: 74, cv_level: "SILVER", id: "b" }, C: { nome: "Cliente", cognome: "C", cv_level: "BRONZE", id: "c" }, D: { nome: "Cliente", cognome: "D", cv_level: "SILVER", id: "d" }, E: { nome: "Cliente", cognome: "E", cv_level: "GOLD", id: "e" }, F: { nome: "Nuovo", cognome: "Cliente", cv_level: "NEW", id: "f" } });
        setPhase("playing");
      }
    })();
  }, []);

  // ---- timeline delle scene (layout = SOLO i cambi; le presenze persistono) ----
  const scenes = useMemo(() => {
    if (!chars) return [];
    const nm = (r) => `${chars[r].nome} ${chars[r].cognome}`;
    const cvB = Math.round(chars.B.cv_score || 74);
    const balA = chars.A.balance ?? 18;
    return [
      { dur: 5000, clock: "08:00", overlay: "intro", narr: "Benvenuto. In pochi minuti ti mostrerò come STELLA CARD PLUS accompagna una normale giornata di attività in una Gaming Hall." },
      { dur: 4500, clock: "08:00", overlay: "open", narr: "La Gaming Hall apre e Stella Card PLUS è pronto ad accompagnare la giornata.", layout: {} },
      { dur: 5000, clock: "10:12", narr: `I clienti entrano e vengono riconosciuti dalla tessera. ${nm("C")} entra dall'ingresso.`, layout: { C: "INGRESSO" } },
      { dur: 5000, clock: "10:46", narr: `Arrivano altri clienti: ${nm("D")} e ${nm("E")}. Ognuno con la sua tessera.`, layout: { C: "SLOT", D: "INGRESSO", E: "INGRESSO" } },
      { dur: 6000, clock: "11:28", narr: `Anche ${nm("F")} entra. I clienti si distribuiscono nelle diverse aree della sala.`, layout: { D: "BAR", E: "SCOMMESSE", F: "INGRESSO" } },
      { dur: 6000, clock: "12:40", narr: "Stella Card PLUS registra presenza, permanenza e frequenza di ogni cliente presente in sala.", layout: { C: "BAR", D: "SCOMMESSE", E: "VLT", F: "SLOT" } },
      { dur: 5500, clock: "18:14", narr: `${nm("B")} entra e raggiunge l'area VLT.`, focus: "B", layout: { B: "VLT", E: "SLOT" } },
      { dur: 5000, clock: "19:37", narr: `Dopo la permanenza in Sala VLT, ${nm("B")} raggiunge il Desk.`, focus: "B", layout: { B: "DESK" } },
      { dur: 5500, clock: "19:37", overlay: "ticket", narr: "Al Desk il cliente consegna il ticket VLT: lo scanner lo legge automaticamente.", focus: "B" },
      { dur: 7000, clock: "19:38", overlay: "cv", narr: "Il ticket alimenta il Customer Value, un indicatore interno della relazione con il cliente.", focus: "B", cvFrom: cvB, cvTo: Math.min(cvB + 4, 100) },
      { dur: 6500, clock: "19:38", overlay: "cvinfo", narr: "Il Customer Value è solo per uso interno: non è visibile al cliente, non è spendibile, non è un premio." },
      { dur: 8000, clock: "19:40", overlay: "split", narr: "Stelle e Customer Value sono due sistemi completamente separati. I dati del ticket NON generano Stelle." },
      { dur: 5500, clock: "20:15", overlay: "catalog", narr: `Vediamo ora il riscatto di un premio. ${nm("A")} entra e ha abbastanza Stelle.`, focus: "A", layout: { A: "BAR" } },
      { dur: 8000, clock: "20:16", overlay: "redeem", narr: `${nm("A")} riscatta "${rewardDemo.nome}" (${rewardDemo.points} ⭐). Il saldo mostrato scende — ma è solo simulazione.`, focus: "A", balFrom: balA, balTo: Math.max((balA) - rewardDemo.points, 0) },
      { dur: 6000, clock: "20:16", overlay: "redeeminfo", narr: "Le Stelle sono il programma loyalty, completamente separate dal Customer Value interno." },
      { dur: 6000, clock: "21:42", overlay: "exit", narr: `${nm("D")} termina la visita e si dirige verso l'uscita: presenza e permanenza vengono registrate.`, focus: "D", layout: { A: "SCOMMESSE", D: "USCITA" } },
      { dur: 6000, clock: "22:05", narr: "La sala resta viva: i clienti presenti si spostano tra le aree e utilizzano i servizi.", layout: { C: "VLT", E: "BAR", F: "SLOT", B: "SCOMMESSE" } },
      { dur: 7000, clock: "22:30", overlay: "livedash", narr: "In ogni momento la struttura vede la situazione live: presenze reali, aree, ticket analizzati e premi." },
      { dur: 7000, clock: "22:50", overlay: "cvlive", narr: "E la classifica interna Customer Value, per cluster: Bronze, Silver, Gold. Uso esclusivamente interno." },
      { dur: 6000, clock: "23:45", overlay: "closing", narr: "La giornata sta terminando: i clienti lasciano progressivamente la struttura.", layout: { A: "USCITA", B: "USCITA" } },
      { dur: 5000, clock: "23:52", narr: `Anche ${nm("C")} ed ${nm("E")} salutano e si dirigono verso l'uscita.`, layout: { C: "USCITA", E: "USCITA" } },
      { dur: 4500, clock: "23:58", narr: `${nm("F")} è l'ultimo cliente a lasciare la sala.`, focus: "F", layout: { F: "USCITA" } },
      { dur: 4500, clock: "00:00", overlay: "closed", narr: "Tutti i clienti hanno lasciato la struttura. La giornata è terminata.", layout: {} },
      { dur: 8500, clock: "00:00", overlay: "summary", narr: "Ecco il riepilogo della giornata: i numeri corrispondono a ciò che hai visto in sala." },
      { dur: 9000, clock: "", overlay: "conclusion", narr: "Presenze, permanenza, loyalty, premi e Customer Value in un unico ecosistema. Le Stelle gestiscono la loyalty; il Customer Value resta un indicatore interno." },
      { dur: 999999, clock: "", overlay: "final", narr: "" },
    ];
  }, [chars, rewardDemo]);

  // ---- stato persistente della sala fino alla scena idx ----
  const computeState = useCallback((idx) => {
    const pos = {};
    ROLES.forEach((r) => { pos[r] = { zone: "OUT", present: false }; });
    const seen = new Set();
    for (let s = 0; s <= idx && s < scenes.length; s++) {
      const lay = scenes[s].layout || {};
      Object.entries(lay).forEach(([role, zone]) => {
        if (!pos[role]) return;
        pos[role] = { zone, present: true };
        seen.add(role);
      });
      // chi è arrivato all'uscita in una scena precedente ha già lasciato la sala
      if (s < idx) {
        ROLES.forEach((r) => { if (pos[r].zone === "USCITA") pos[r] = { zone: "OUT", present: false }; });
      }
    }
    return { pos, entered: seen.size };
  }, [scenes]);

  // avanzamento scene
  useEffect(() => {
    if (phase !== "playing" || !scenes.length) return;
    if (i >= scenes.length - 1) { setPhase("done"); return; }
    timerRef.current = setTimeout(() => setI((x) => x + 1), scenes[i].dur);
    return () => clearTimeout(timerRef.current);
  }, [i, phase, scenes]);

  // effetto typing
  const scene = scenes[i] || {};
  useEffect(() => {
    setTyped("");
    const text = scene.narr || "";
    let k = 0;
    const id = setInterval(() => { k++; setTyped(text.slice(0, k)); if (k >= text.length) clearInterval(id); }, 22);
    return () => clearInterval(id);
  }, [i, scene.narr]);

  const replay = useCallback(() => { setI(0); setPhase("playing"); }, []);

  // stato corrente della sala
  const state = useMemo(() => computeState(i), [computeState, i]);
  const presentRoles = ROLES.filter((r) => state.pos[r].present);
  const zoneCount = (z) => presentRoles.filter((r) => state.pos[r].zone === z).length;
  const stats = {
    present: presentRoles.length,
    entered: state.entered,
    vlt: zoneCount("VLT"), slot: zoneCount("SLOT"), scommesse: zoneCount("SCOMMESSE"), bar: zoneCount("BAR"),
  };
  const occupied = new Set(presentRoles.map((r) => state.pos[r].zone));

  const avatarPos = (role) => {
    const { zone, present } = state.pos[role];
    const base = CENTER[zone] || CENTER.OUT;
    const idx = ROLES.indexOf(role);
    const dx = (idx % 3) * 30 - 30, dy = Math.floor(idx / 3) * 34 - 10;
    return { x: base[0] + dx, y: base[1] + dy, present };
  };

  const progress = Math.min((i / (scenes.length - 1)) * 100, 100);

  if (phase === "loading" || !chars) {
    return <div className="min-h-screen bg-zinc-950 flex items-center justify-center text-zinc-400">Preparazione della demo…</div>;
  }

  return (
    <div className="min-h-screen bg-[#08080b] text-white overflow-hidden relative">
      {/* top bar */}
      <div className="flex items-center gap-3 px-6 py-4 relative z-20">
        <Star className="w-6 h-6 text-sky-400 fill-sky-400" />
        <span className="font-display font-bold text-lg">STELLA CARD <span className="text-sky-400">PLUS</span></span>
        <span className="text-xs text-zinc-500 hidden sm:inline">· Live Demo</span>
        <div className="ml-auto flex items-center gap-3">
          <div data-testid="demo-present" className="hidden sm:flex items-center gap-1.5 text-xs px-3 py-1 rounded-lg bg-zinc-900 border border-zinc-800 text-emerald-300">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> In sala: {stats.present}
          </div>
          <div data-testid="demo-clock" className="font-mono text-lg tabular-nums px-3 py-1 rounded-lg bg-zinc-900 border border-zinc-800 text-amber-300">
            {scene.clock || "00:00"}
          </div>
          <button data-testid="demo-home" onClick={() => navigate("/")} className="flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white px-3 py-1.5 rounded-lg border border-zinc-800">
            <Home className="w-4 h-4" /> Home
          </button>
        </div>
      </div>
      {/* progress */}
      <div className="h-1 bg-zinc-900 mx-6 rounded-full overflow-hidden relative z-20">
        <motion.div className="h-full bg-gradient-to-r from-sky-500 to-amber-400" animate={{ width: `${progress}%` }} transition={{ ease: "linear" }} />
      </div>

      {/* STAGE */}
      <div className="relative mx-auto mt-4" style={{ width: "min(960px, 96vw)", aspectRatio: "960/520" }}>
        <div className="absolute inset-0" style={{ transformOrigin: "top left" }}>
          <div className="relative w-full h-full">
            <svg viewBox="0 0 960 520" className="w-full h-full">
              <defs>
                <pattern id="dg" width="24" height="24" patternUnits="userSpaceOnUse"><path d="M24 0 L0 0 0 24" fill="none" stroke="#15151b" strokeWidth="1" /></pattern>
              </defs>
              <rect width="960" height="520" fill="url(#dg)" rx="18" />
              {Object.entries(ZONES).map(([k, z]) => {
                const active = occupied.has(k) || (scene.focus && state.pos[scene.focus]?.zone === k);
                return (
                  <g key={k}>
                    <rect x={z.x} y={z.y} width={z.w} height={z.h} rx="12" fill={`${z.c}${active ? "26" : "12"}`} stroke={z.c} strokeWidth={active ? 2.5 : 1.2} />
                    <text x={z.x + 12} y={z.y + 24} fill={z.c} fontSize="14" fontWeight="700" fontFamily="Space Grotesk, sans-serif">{z.label}</text>
                  </g>
                );
              })}
            </svg>

            {/* avatars overlay (percent positioning over the 960x520 box) */}
            {chars && ROLES.map((role) => {
              const p = avatarPos(role);
              const c = chars[role];
              const col = CLUSTER_COLOR[c.cv_level] || "#9ca3af";
              const focused = scene.focus === role && p.present;
              const cardOverlay = scene.overlay && scene.overlay !== "open" && scene.overlay !== "closed";
              const targetOpacity = p.present ? (cardOverlay ? 0.3 : 1) : 0;
              return (
                <motion.div key={role} data-testid={`demo-avatar-${role}`}
                  className="absolute" style={{ left: 0, top: 0, zIndex: focused ? 10 : 5 }}
                  initial={false}
                  animate={{ left: `${(p.x / 960) * 100}%`, top: `${(p.y / 520) * 100}%`, opacity: targetOpacity, scale: focused && !cardOverlay ? 1.18 : 1 }}
                  transition={{ duration: 0.8, ease: "easeInOut" }}>
                  <div className="relative -translate-x-1/2 -translate-y-1/2 flex flex-col items-center">
                    {focused && <div className="absolute -inset-2 rounded-full animate-ping" style={{ background: `${col}55` }} />}
                    <div className="w-9 h-9 rounded-full flex items-center justify-center text-[11px] font-bold shadow-lg relative"
                      style={{ background: "#18181f", border: `2.5px solid ${col}`, color: col }}>
                      {initials(c)}
                    </div>
                    <div className="mt-1 px-1.5 py-0.5 rounded bg-black/70 text-[9px] whitespace-nowrap" style={{ color: col }}>
                      {c.nome} · {c.cv_level || "—"}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* CENTER OVERLAYS */}
        <AnimatePresence mode="wait">
          {scene.overlay && scene.overlay !== "open" && scene.overlay !== "closed" && (
            <Overlay key={`${i}-${scene.overlay}`} scene={scene} chars={chars} reward={rewardDemo} stats={stats} />
          )}
        </AnimatePresence>

        {/* OPEN / CLOSED banners */}
        <AnimatePresence>
          {scene.overlay === "open" && <Banner key="open" icon={<DoorOpen className="w-8 h-8" />} text="ESERCIZIO APERTO" color="#22c55e" />}
          {scene.overlay === "closed" && <Banner key="closed" icon={<DoorClosed className="w-8 h-8" />} text="ESERCIZIO CHIUSO 🔒" color="#f87171" />}
        </AnimatePresence>
      </div>

      {/* NARRATORE */}
      <div className="fixed bottom-5 right-5 z-30 max-w-sm flex items-end gap-3">
        <AnimatePresence>
          {scene.narr && (
            <motion.div key={i} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              data-testid="demo-narrator" className="bg-zinc-900/95 backdrop-blur border border-sky-400/30 rounded-2xl rounded-br-sm p-3.5 text-sm leading-snug shadow-2xl">
              {typed}<span className="animate-pulse">▍</span>
            </motion.div>
          )}
        </AnimatePresence>
        <div className="w-14 h-14 shrink-0 rounded-full bg-gradient-to-br from-sky-500 to-sky-700 border-2 border-sky-300 flex items-center justify-center text-2xl shadow-xl" title="Consulente Stella Card PLUS">
          <span role="img" aria-label="narratore">🧑‍💼</span>
        </div>
      </div>

      {/* END CONTROLS */}
      <AnimatePresence>
        {phase === "done" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="fixed bottom-5 left-5 z-30 flex gap-2">
            <button data-testid="demo-replay" onClick={replay} className="flex items-center gap-2 px-5 h-11 rounded-xl bg-sky-500 hover:bg-sky-400 text-white font-semibold text-sm">
              <RotateCcw className="w-4 h-4" /> Rivedi Demo
            </button>
            <button data-testid="demo-back-home" onClick={() => navigate("/")} className="flex items-center gap-2 px-5 h-11 rounded-xl bg-zinc-800 border border-zinc-700 hover:border-zinc-500 text-sm">
              <Home className="w-4 h-4" /> Torna alla Home
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function Banner({ icon, text, color }) {
  return (
    <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 1.1 }}
      className="absolute inset-0 flex items-center justify-center pointer-events-none">
      <div className="flex items-center gap-3 px-8 py-5 rounded-2xl bg-zinc-950/80 border-2" style={{ borderColor: color, color }}>
        {icon}<span className="font-display font-bold text-2xl tracking-wide">{text}</span>
      </div>
    </motion.div>
  );
}

function Card({ children, className = "" }) {
  return (
    <motion.div initial={{ opacity: 0, scale: 0.92, y: 10 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.4 }}
      className={`absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-zinc-900/95 backdrop-blur border border-zinc-700 rounded-2xl shadow-2xl p-6 ${className}`}>
      {children}
    </motion.div>
  );
}

function Overlay({ scene, chars, reward, stats }) {
  const o = scene.overlay;
  const nm = (r) => `${chars[r]?.nome || ""} ${chars[r]?.cognome || ""}`;
  if (o === "intro") return (
    <Card className="text-center w-[80%] max-w-lg">
      <div className="font-display text-4xl font-bold">STELLA CARD <span className="text-sky-400">PLUS</span></div>
      <div className="text-amber-300 mt-2 tracking-widest text-sm">UNA GIORNATA CON STELLA CARD PLUS</div>
    </Card>
  );
  if (o === "ticket") return (
    <Card className="w-[300px]">
      <div className="flex items-center gap-2 text-amber-300 mb-3"><TicketIcon className="w-5 h-5" /> <b>Ticket VLT</b></div>
      <div className="text-sm space-y-1 text-zinc-300">
        <div>Cliente: <b className="text-white">{nm("B")}</b></div>
        <div>Importo: <b className="text-white">€185,00</b></div>
        <div>Ora: <b className="text-white">19:37</b> · Area: <b className="text-white">VLT</b></div>
        <div>Durata sessione: <b className="text-white">1h 23m</b></div>
      </div>
      <div className="mt-4 flex items-center gap-2 text-sky-300 text-sm"><ScanLine className="w-4 h-4 animate-pulse" /> Lettura ticket… <span className="text-green-400 font-semibold ml-auto">TICKET LETTO ✓</span></div>
    </Card>
  );
  if (o === "cv") return (
    <Card className="w-[320px] text-center">
      <div className="text-xs tracking-widest text-zinc-500">CUSTOMER VALUE · {nm("B")}</div>
      <div className="my-3"><CountUp from={scene.cvFrom} to={scene.cvTo} className="font-display text-6xl font-bold text-sky-400" /><span className="text-zinc-500 text-xl"> / 100</span></div>
      <div className="inline-flex items-center gap-1 text-green-400 font-semibold"><TrendingUp className="w-4 h-4" /> +{scene.cvTo - scene.cvFrom} Customer Value</div>
      <div className="text-[11px] text-zinc-500 mt-2">(incremento solo simulato — nessuna scrittura sul database)</div>
    </Card>
  );
  if (o === "cvinfo") return (
    <Card className="w-[340px] text-center border-sky-400/40">
      <div className="font-display text-2xl font-bold text-sky-400">CUSTOMER VALUE</div>
      <div className="mt-3 space-y-1.5 text-sm text-zinc-300">
        <div className="flex items-center justify-center gap-2"><Lock className="w-4 h-4 text-sky-400" /> Solo uso interno</div>
        <div>Non visibile al cliente</div><div>Non spendibile · Non è un premio</div>
      </div>
    </Card>
  );
  if (o === "split") return (
    <Card className="w-[92%] max-w-2xl">
      <div className="grid grid-cols-2 gap-4">
        <div className="rounded-xl border border-amber-400/40 bg-amber-400/5 p-4">
          <div className="flex items-center gap-2 text-amber-400 font-bold"><Star className="w-5 h-5 fill-current" /> STELLE · Loyalty</div>
          <ul className="mt-2 text-xs text-zinc-300 space-y-1"><li>✓ Visibili al cliente</li><li>✓ Riscattabili a catalogo</li><li>✓ Saldo spendibile</li></ul>
        </div>
        <div className="rounded-xl border border-sky-400/40 bg-sky-400/5 p-4">
          <div className="flex items-center gap-2 text-sky-400 font-bold"><Lock className="w-5 h-5" /> CUSTOMER VALUE · Interno</div>
          <ul className="mt-2 text-xs text-zinc-300 space-y-1"><li>• Non visibile al cliente</li><li>• Non spendibile</li><li>• Solo analisi interna</li></ul>
        </div>
      </div>
      <div className="mt-4 text-center text-red-300 font-bold text-sm border border-red-500/40 rounded-lg py-2 bg-red-500/5">⛨ I DATI DEL TICKET NON GENERANO STELLE</div>
    </Card>
  );
  if (o === "catalog") return (
    <Card className="w-[300px]">
      <div className="flex items-center gap-2 text-amber-300 mb-3"><Gift className="w-5 h-5" /> <b>Catalogo Premi</b></div>
      <div className="space-y-2 text-sm">
        {[["Caffè", 2], [reward.nome, reward.points], ["Aperitivo", 8], ["Gadget", 15]].map(([n, p], k) => (
          <div key={k} className={`flex justify-between px-3 py-1.5 rounded-lg ${n === reward.nome ? "bg-amber-400/15 border border-amber-400/40" : "bg-zinc-800/60"}`}>
            <span>{n}</span><span className="text-amber-400 font-semibold">⭐ {p}</span>
          </div>
        ))}
      </div>
    </Card>
  );
  if (o === "redeem") return (
    <Card className="w-[340px] text-center">
      <div className="text-xs tracking-widest text-zinc-500">RISCATTO · {nm("A")}</div>
      <div className="my-3 flex justify-center">
        {reward.image
          ? <img data-testid="demo-reward-image" src={imgUrl(reward.image)} alt={reward.nome} className="w-24 h-24 object-cover rounded-2xl border-2 border-amber-400/50 shadow-lg" />
          : <div data-testid="demo-reward-image" className="w-24 h-24 rounded-2xl border-2 border-amber-400/50 bg-amber-400/10 flex items-center justify-center"><Gift className="w-10 h-10 text-amber-400" /></div>}
      </div>
      <div className="font-display text-lg font-bold text-white">{reward.nome}</div>
      <div className="my-2 flex items-center justify-center gap-3">
        <Star className="w-6 h-6 text-amber-400 fill-amber-400" />
        <CountUp from={scene.balFrom} to={scene.balTo} className="font-display text-5xl font-bold text-amber-400" />
      </div>
      <div className="text-green-400 font-semibold text-sm">Premio riscattato (−{reward.points} ⭐)</div>
      <div className="text-[11px] text-zinc-500 mt-1">Saldo {scene.balFrom} → {scene.balTo} · variazione solo visiva (DB invariato)</div>
    </Card>
  );
  if (o === "redeeminfo") return (
    <Card className="w-[340px] text-center">
      <Star className="w-8 h-8 text-amber-400 fill-amber-400 mx-auto" />
      <div className="mt-2 text-sm text-zinc-300">Le <b className="text-amber-400">Stelle</b> sono loyalty, separate dal <b className="text-sky-400">Customer Value</b> interno.</div>
    </Card>
  );
  if (o === "exit") return (
    <Card className="w-[300px] text-center border-red-400/30">
      <div className="text-red-300 font-bold">{nm("D")} lascia la Gaming Hall</div>
      <div className="text-sm text-zinc-400 mt-2">Visita terminata · presenza e permanenza registrate</div>
    </Card>
  );
  if (o === "livedash") return (
    <Card className="w-[92%] max-w-xl">
      <div className="text-xs tracking-widest text-sky-400 mb-3 flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" /> LIVE</div>
      <div className="grid grid-cols-4 gap-3 text-center">
        {[["In sala", stats.present], ["Ingressi", stats.entered], ["VLT", stats.vlt], ["Slot", stats.slot], ["Scommesse", stats.scommesse], ["Bar", stats.bar], ["Ticket", 1], ["Premi", 1]].map(([l, v], k) => (
          <div key={k} className="rounded-xl bg-zinc-800/60 p-3"><div className="font-display text-2xl font-bold text-sky-300">{v}</div><div className="text-[10px] text-zinc-500 mt-1">{l}</div></div>
        ))}
      </div>
    </Card>
  );
  if (o === "cvlive") {
    const rows = ["E", "B", "D", "C", "F"].map((r) => chars[r]).filter(Boolean);
    return (
      <Card className="w-[92%] max-w-md">
        <div className="text-xs tracking-widest text-sky-400 mb-3">CUSTOMER VALUE LIVE · interno</div>
        <div className="space-y-2">
          {rows.map((c, k) => {
            const col = CLUSTER_COLOR[c.cv_level] || "#9ca3af";
            const v = Math.round(c.cv_score || (90 - k * 15));
            return (
              <div key={k} className="flex items-center gap-3">
                <span className="text-sm w-32 truncate">{c.nome} {c.cognome}</span>
                <div className="flex-1 h-2.5 rounded-full bg-zinc-800 overflow-hidden"><motion.div className="h-full" style={{ background: col }} initial={{ width: 0 }} animate={{ width: `${v}%` }} transition={{ duration: 1.2 }} /></div>
                <span className="text-xs font-bold w-8 text-right" style={{ color: col }}>{v}</span>
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ color: col, background: `${col}22` }}>{c.cv_level}</span>
              </div>
            );
          })}
        </div>
      </Card>
    );
  }
  if (o === "closing") return (
    <Card className="text-center w-[280px]"><div className="text-zinc-300">Chiusura in corso…</div><div className="font-display text-4xl font-bold text-red-300 mt-2">In sala: {stats.present}</div><div className="text-[11px] text-zinc-500 mt-2">I clienti stanno uscendo</div></Card>
  );
  if (o === "summary") {
    const clusters = {};
    Object.values(chars).forEach((c) => { const l = c.cv_level || "—"; clusters[l] = (clusters[l] || 0) + 1; });
    const clusterTiles = Object.entries(clusters).sort((a, b) => b[1] - a[1]).slice(0, 3);
    const tiles = [
      ["Clienti unici", stats.entered], ["Ingressi", stats.entered], ["Uscite", stats.entered],
      ["Ticket analizzati", 1], ["Premi riscattati", 1], ["Stelle usate", `${reward.points} ⭐`],
      ...clusterTiles.map(([l, v]) => [l.charAt(0) + l.slice(1).toLowerCase(), v]),
    ];
    return (
      <Card className="w-[92%] max-w-xl">
        <div className="font-display text-xl font-bold text-center mb-4">Riepilogo Giornata</div>
        <div className="grid grid-cols-3 gap-3 text-center">
          {tiles.map(([l, v], k) => (
            <div key={k} className="rounded-xl bg-zinc-800/60 p-3"><div className="font-display text-xl font-bold text-amber-300">{v}</div><div className="text-[10px] text-zinc-500 mt-1">{l}</div></div>
          ))}
        </div>
      </Card>
    );
  }
  if (o === "conclusion") return (
    <Card className="text-center w-[85%] max-w-lg">
      <div className="font-display text-2xl font-bold">Una giornata con <span className="text-sky-400">Stella Card PLUS</span></div>
      <div className="mt-3 text-sm text-zinc-300">Le <b className="text-amber-400">Stelle</b> gestiscono la loyalty. Il <b className="text-sky-400">Customer Value</b> resta un indicatore esclusivamente interno.</div>
    </Card>
  );
  if (o === "final") return (
    <Card className="text-center w-[80%] max-w-lg border-sky-400/40">
      <Star className="w-10 h-10 text-sky-400 fill-sky-400 mx-auto" />
      <div className="font-display text-3xl font-bold mt-2">STELLA CARD <span className="text-sky-400">PLUS</span></div>
      <div className="text-amber-300 tracking-widest text-sm mt-2">CONOSCI. PREMIA. FIDELIZZA.</div>
      <div className="text-zinc-500 text-xs mt-3">Stella Card PLUS al lavoro</div>
    </Card>
  );
  return null;
}
